import { Component } from "react";
import { message, PageHeader, Spin, Upload, Row, Col, Alert, Switch } from "antd";
import { connect } from "react-redux";
import axios from "axios";
import { TableItem, ButtonItem } from "../../../../Components/Items";
import { UploadOutlined, DownloadOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx-js-style";
import { encodeId, decodeId } from "../../../../Components/idEncoder";

const BASE_LABELS = {
  1: "Sueldo",
  2: "Comisión",
  3: "Destajo",
  4: "Sueldo-Comisión",
  5: "Sueldo-Destajo",
};
const ESTADO_CIVIL_LABELS = {
  1: "Soltero",
  2: "Casado",
  3: "Concubinato",
  4: "Union Civil",
  5: "Viudo",
  6: "Divorciado",
};
const AVISOS_PENDIENTES_LABELS = {
  alta: "Alta",
  baja: "Baja",
  modifSalario: "Modificacion de Salario",
};
const SEXO_VALORES = ["Hombre", "Mujer", "No Aplica"];

// ── Campos de texto libre (passthrough) ─────────────────────────────────────
const CAMPO_TEXTO = {
  Nombre: "nombre",
  "Apellido Paterno": "apellidoPaterno",
  "Apellido Materno": "apellidoMaterno",
  RFC: "rfc",
  "Registro ID Tributario": "registroIDTributario",
  "Numero Licencia": "numLicencia",
  Colonia: "colonia",
  "Descripcion Direccion": "descripcion",
  Calle: "calle",
  "Numero Calle": "numeroCalle",
  "Numero Interior": "numeroInterior",
  Referencia: "referencia",
  "Correo Electronico": "correoElectronico",
  Expediente: "expediente",
  "Nombre Padre": "nombrePadre",
  UMF: "umf",
  "Nombre Madre": "nombreMadre",
  "Ultima Baja": "ultimaBaja",
  "Ciudad Nacimiento": "ciudadNacimientoId",
  CURP: "curp",
  Extra1: "extra1",
  "Numero de Cuenta": "numCuenta",
  "Clabe Interbancaria": "clabeInterbancaria",
  "Sucursal Pago Electronico": "sucursalPagoElectronico",
  "Numero Seguridad Social": "numSeguridadSocial",
};
const COLUMNAS_TEXTO = Object.keys(CAMPO_TEXTO);
const COLUMNAS_TEXTO_REQUERIDAS = new Set(["Nombre", "Apellido Paterno"]);

// ── Campos numéricos ─────────────────────────────────────────────────────────
const CAMPO_NUMERO = {
  "Salario Diario": "salarioDiario",
  "SBC Parte Fija": "sbcParteFija",
  "SBC Parte Variable": "sbcParteVariable",
  "SBC Topado 25 UMAS": "sbcTopado25UMAS",
  "Numero Extra1": "numExtra1",
  "Salario Variable": "salarioVariable",
  "Salario Promedio": "salarioPromedio",
  "Salario Base Liquidacion": "salarioBaseLiquidacion",
  "Saldo Ajuste Neto": "saldoAjusteNeto",
  Afore: "afore",
  "Numero FONACOT": "numeroFONACOT",
};
const COLUMNAS_NUMERO = Object.keys(CAMPO_NUMERO);

// ── Campos de fecha (formato AAAA-MM-DD) ────────────────────────────────────
const CAMPO_FECHA = {
  "Fecha Alta": "fechaAlta",
  "Fecha Reingreso": "fechaReingreso",
  "Vigencia Licencia": "vigenciaLicencia",
  "Fecha Nacimiento": "fechaNacimiento",
  "Fecha Salario Variable": "fechaSalarioVariable",
  "Fecha Salario Promedio": "fechaSalarioPromedio",
  "Fecha Salario Diario": "fechaSalarioDiario",
  "Fecha Salario Integrado": "fechaSalarioIntegrado",
};
const COLUMNAS_FECHA = Object.keys(CAMPO_FECHA);

// ── Campos booleanos (SI/NO) ─────────────────────────────────────────────────
const CAMPO_BOOL = {
  Extranjero: "extranjero",
  Chofer: "chofer",
  Sindicalizado: "sindicalizado",
  "Extranjero Sin Curp": "extranjeroSinCurp",
  "Calculo PTU": "calculoPTU",
  "Calculo Aguinaldo": "calculoAguinaldo",
  "Responsable Mezcal": "responsableMezcal",
  Externo: "externo",
};
const COLUMNAS_BOOL = Object.keys(CAMPO_BOOL);

// ── Enums numéricos (columna muestra la etiqueta, se guarda la clave) ───────
const CAMPO_ENUM_NUM = {
  "Base Pago": { campo: "basePago", labels: BASE_LABELS },
  "Base de Cotizacion": { campo: "baseDeCotizacion", labels: BASE_LABELS },
  "Estado Civil": { campo: "estadoCivil", labels: ESTADO_CIVIL_LABELS },
};
const COLUMNAS_ENUM_NUM = Object.keys(CAMPO_ENUM_NUM);

// ── Enums de texto (valor tal cual o mapeado a una etiqueta) ────────────────
const CAMPO_ENUM_STR = {
  Sexo: { campo: "sexo", valores: SEXO_VALORES },
  "Avisos Pendientes": { campo: "avisosPendientes", labels: AVISOS_PENDIENTES_LABELS },
};
const COLUMNAS_ENUM_STR = Object.keys(CAMPO_ENUM_STR);

// ── Catálogos: la columna muestra el nombre/descripción, se resuelve al
// ObjectId correspondiente contra el catálogo ya cargado en el estado ───────
const CATALOGOS_CONFIG = [
  { col: "Departamento", campo: "departamentoId", stateKey: "todosDepartamentos", matchField: "nombre" },
  { col: "Puesto", campo: "puestoId", stateKey: "todosPuestos", matchField: "nombre" },
  { col: "Turno", campo: "turnoId", stateKey: "todosTurnos", matchField: "nombre" },
  { col: "Tipo Contrato", campo: "tipoContratoId", stateKey: "todosTiposContrato", matchField: "Descripcion", claveField: "c_TipoContrato" },
  { col: "Tipo Periodo", campo: "tipoPeriodoId", stateKey: "todosTiposPeriodo", matchField: "nombre" },
  { col: "Forma de Pago", campo: "formaDePagoId", stateKey: "todasFormasDePago", matchField: "descripcion", claveField: "c_FormaPago" },
  { col: "Tipo Prestacion", campo: "tipoPrestacionId", stateKey: "todosTiposPrestacion", matchField: "nombre" },
  { col: "Banco", campo: "bancoPagoElectronicoId", stateKey: "todosBancos", matchField: "nombre" },
  { col: "Registro Patronal", campo: "registroPatronalId", stateKey: "todosRegistrosPatronales", matchField: "registroPatronalIMSS" },
  { col: "Regimen Fiscal", campo: "regimenFiscalId", stateKey: "todosRegimenFiscal", matchField: "descripcion", claveField: "c_RegimenFiscal" },
  { col: "Tipo Jornada", campo: "tipoJornadaId", stateKey: "todosTiposJornada", matchField: "Descripcion", claveField: "c_TipoJornada" },
  { col: "Pais Residencia", campo: "paisResidenciaId", stateKey: "todosPaises", matchField: "nombre" },
  { col: "Figura Transporte", campo: "figuraTransporteId", stateKey: "todasFigurasTransporte", matchField: "Descripcion" },
  { col: "Estado Nacimiento", campo: "estadoNacimientoId", stateKey: "todosEstados", matchField: "nombre", claveField: "abreviatura" },
  { col: "Tipo Regimen", campo: "tipoRegimenId", stateKey: "todosTiposRegimen", matchField: "descripcion", claveField: "c_TipoRegimen" },
];

// El Excel se llena a mano, así que comparar con === contra la descripción exacta
// del catálogo rechazaba valores que sí existen: acentos ("Queretaro" vs
// "Querétaro"), mayúsculas y espacios dobles. Se normaliza igual que en
// ImportarEmpleados (resolverCatalogo).
const normalizarCatalogo = (v) =>
  String(v ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

// Índice llave→item de un catálogo. Además de la descripción acepta la clave
// (c_RegimenFiscal, c_TipoContrato, abreviatura del Estado…) y la forma
// compuesta "clave - descripción", que es como el resto del ERP muestra estos
// catálogos —incluido el select de Editar Empleado—, así que es lo que el
// usuario copia al Excel. La descripción se indexa en una primera pasada para
// que siempre gane si una clave choca con el nombre de otro registro.
const construirIndiceCatalogo = (lista, cfg) => {
  const indice = new Map();
  const agregar = (llave, item) => {
    const k = normalizarCatalogo(llave);
    if (k && !indice.has(k)) indice.set(k, item);
  };

  for (const item of lista) agregar(item[cfg.matchField], item);

  if (!cfg.claveField) return indice;

  for (const item of lista) {
    const clave = item[cfg.claveField];
    if (clave === undefined || clave === null || clave === "") continue;
    const claveStr = String(clave).trim();
    // Las claves SAT viven como Number en algunos catálogos (c_FormaPago) y como
    // String con cero a la izquierda en otros (c_TipoContrato "01"). Se aceptan
    // las dos escrituras sin importar de cuál se trate.
    const variantes = [claveStr];
    if (/^\d+$/.test(claveStr)) {
      variantes.push(String(Number(claveStr)), String(Number(claveStr)).padStart(2, "0"));
    }
    for (const v of variantes) {
      agregar(v, item);
      agregar(`${v} - ${item[cfg.matchField]}`, item);
      agregar(`${v} ${item[cfg.matchField]}`, item);
    }
  }
  return indice;
};

const TODAS_EDITABLES = [
  ...COLUMNAS_TEXTO,
  ...COLUMNAS_NUMERO,
  ...COLUMNAS_FECHA,
  ...COLUMNAS_BOOL,
  ...COLUMNAS_ENUM_NUM,
  ...COLUMNAS_ENUM_STR,
  ...CATALOGOS_CONFIG.map((c) => c.col),
  "Codigo Postal",
  "Codigo",
  "Lider Directo",
];

const nombreCompleto = (emp) =>
  [emp.nombre, emp.apellidoPaterno, emp.apellidoMaterno].filter(Boolean).join(" ");

const fechaISO = (d) => (d ? new Date(d).toISOString().slice(0, 10) : "");

const boolVal = (v) => {
  const s = String(v).trim().toLowerCase();
  if (s === "si" || s === "sí") return true;
  if (s === "no") return false;
  return null;
};

const buscarEtiqueta = (labels, val) => {
  const entry = Object.entries(labels).find(
    ([, label]) => label.toLowerCase() === val.toLowerCase()
  );
  return entry || null;
};

const HEADER_STYLE = {
  fill: { fgColor: { rgb: "00BFD6" } },
  font: { bold: true, color: { rgb: "FFFFFF" }, sz: 10, name: "Arial" },
  alignment: { horizontal: "center", vertical: "center", wrapText: true },
  border: {
    top: { style: "thin", color: { rgb: "C0C0C0" } },
    bottom: { style: "thin", color: { rgb: "C0C0C0" } },
    left: { style: "thin", color: { rgb: "C0C0C0" } },
    right: { style: "thin", color: { rgb: "C0C0C0" } },
  },
};

const aplicarEstiloEncabezado = (ws) => {
  if (!ws["!ref"]) return;
  const range = XLSX.utils.decode_range(ws["!ref"]);
  for (let c = range.s.c; c <= range.e.c; c++) {
    const cellRef = XLSX.utils.encode_cell({ r: range.s.r, c });
    if (ws[cellRef]) ws[cellRef].s = HEADER_STYLE;
  }
};

class EdicionMasivaEmpleados extends Component {
  state = {
    loading: false,
    empleadosMap: {},
    todosDepartamentos: [],
    todosPuestos: [],
    todosTurnos: [],
    todosTiposContrato: [],
    todosTiposPeriodo: [],
    todasFormasDePago: [],
    todosTiposPrestacion: [],
    todosBancos: [],
    todosRegistrosPatronales: [],
    todosRegimenFiscal: [],
    todosTiposJornada: [],
    todosPaises: [],
    todasFigurasTransporte: [],
    todosEstados: [],
    todosTiposRegimen: [],
    archivosExcel: [],
    data: [],
    columnasPresentes: [],
    archivoError: null,
    errores: 0,
    soloConCambios: true,
    sinIdError: false,
    erroresBackend: [],
  };

  componentDidMount() {
    this.setState({ loading: true });
    axios
      .post(
        "empleados/datosParaEdicionMasivaEmpleados",
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        const empleadosMap = {};
        if (res.data.success === 2) {
          (res.data.empleados || []).forEach((emp) => {
            empleadosMap[String(emp._id)] = emp;
          });
        }
        this.setState({
          empleadosMap,
          todosDepartamentos: res.data.success === 2 ? res.data.departamentos || [] : [],
          todosPuestos: res.data.success === 2 ? res.data.puestos || [] : [],
          todosTurnos: res.data.success === 2 ? res.data.turnos || [] : [],
          todosTiposContrato: res.data.success === 2 ? res.data.tiposContrato || [] : [],
          todosTiposPeriodo: res.data.success === 2 ? res.data.tiposPeriodo || [] : [],
          todasFormasDePago: res.data.success === 2 ? res.data.formasDePago || [] : [],
          todosTiposPrestacion: res.data.success === 2 ? res.data.tiposPrestacion || [] : [],
          todosBancos: res.data.success === 2 ? res.data.bancos || [] : [],
          todosRegistrosPatronales: res.data.success === 2 ? res.data.registrosPatronales || [] : [],
          todosRegimenFiscal: res.data.success === 2 ? res.data.regimenFiscal || [] : [],
          todosTiposJornada: res.data.success === 2 ? res.data.tiposJornada || [] : [],
          todosPaises: res.data.success === 2 ? res.data.paises || [] : [],
          todasFigurasTransporte: res.data.success === 2 ? res.data.figurasTransporte || [] : [],
          todosEstados: res.data.success === 2 ? res.data.estados || [] : [],
          todosTiposRegimen: res.data.success === 2 ? res.data.tiposRegimen || [] : [],
          loading: false,
        });
      });
  }

  atras() {
    this.props.history.goBack();
  }

  descargarExcel() {
    const { empleadosMap } = this.state;
    const empleados = Object.values(empleadosMap);
    if (!empleados.length) {
      message.warning("No hay empleados para descargar");
      return;
    }

    const wb = XLSX.utils.book_new();

    const rows = empleados.map((emp) => {
      const row = {
        ID: encodeId(String(emp._id)),
        Numero: emp.serieNumero || "",
        "Nombre Completo": nombreCompleto(emp),
      };
      for (const col of COLUMNAS_TEXTO) row[col] = emp[CAMPO_TEXTO[col]] || "";
      for (const col of COLUMNAS_NUMERO) row[col] = emp[CAMPO_NUMERO[col]] ?? "";
      for (const col of COLUMNAS_FECHA) row[col] = fechaISO(emp[CAMPO_FECHA[col]]);
      for (const col of COLUMNAS_BOOL) row[col] = emp[CAMPO_BOOL[col]] ? "SI" : "NO";
      for (const col of COLUMNAS_ENUM_NUM) {
        const { campo, labels } = CAMPO_ENUM_NUM[col];
        row[col] = labels[emp[campo]] || "";
      }
      for (const col of COLUMNAS_ENUM_STR) {
        const cfg = CAMPO_ENUM_STR[col];
        row[col] = cfg.labels ? cfg.labels[emp[cfg.campo]] || "" : emp[cfg.campo] || "";
      }
      for (const cfg of CATALOGOS_CONFIG) {
        row[cfg.col] = emp[cfg.campo]?.[cfg.matchField] || "";
      }
      row["Codigo Postal"] = emp.coloniaId?.cp || "";
      row["Codigo"] = emp.codigo ?? "";
      row["Lider Directo"] = emp.liderDirectoId?.serieNumero || "";
      return row;
    });

    const ws = XLSX.utils.json_to_sheet(rows);
    const headers = rows.length ? Object.keys(rows[0]) : [];
    ws["!cols"] = headers.map((h) => ({ wch: Math.max(h.length + 2, 15) }));
    aplicarEstiloEncabezado(ws);
    XLSX.utils.book_append_sheet(wb, ws, "Empleados");

    const instrucciones = [
      ["Instrucciones de uso"],
      [""],
      ["1. La columna ID es OBLIGATORIA y NO debe modificarse ni eliminarse."],
      ["2. Las columnas Numero y Nombre Completo son solo de referencia y no se pueden editar."],
      ["3. Puede BORRAR las columnas que NO desee editar. Solo se modificarán las columnas presentes en el archivo."],
      ["4. Columnas de catálogo (Departamento, Puesto, Turno, Tipo Contrato, Tipo Periodo, Forma de Pago, Tipo Prestacion, Banco, Registro Patronal, Regimen Fiscal, Tipo Jornada, Pais Residencia, Figura Transporte, Estado Nacimiento, Tipo Regimen): escriba el nombre/descripción tal como aparece en el sistema. No importan acentos ni mayúsculas. Deje la celda vacía para quitar el valor."],
      ["4.1. En Regimen Fiscal, Tipo Regimen, Tipo Contrato, Tipo Jornada y Forma de Pago también se acepta la clave SAT (por ejemplo 605) o la forma \"605 - Descripción\". En Estado Nacimiento también se acepta la abreviatura (por ejemplo QUE)."],
      ["5. Columnas SI/NO (Extranjero, Chofer, Sindicalizado, Extranjero Sin Curp, Calculo PTU, Calculo Aguinaldo, Responsable Mezcal, Externo): use SI o NO."],
      ["6. Columnas de fecha (Fecha Alta, Fecha Reingreso, Vigencia Licencia, Fecha Nacimiento, Fecha Salario Variable, Fecha Salario Promedio, Fecha Salario Diario, Fecha Salario Integrado): use el formato AAAA-MM-DD."],
      ["7. Base Pago y Base de Cotizacion: use Sueldo, Comisión, Destajo, Sueldo-Comisión o Sueldo-Destajo."],
      ["8. Estado Civil: use Soltero, Casado, Concubinato, Union Civil, Viudo o Divorciado."],
      ["9. Sexo: use Hombre, Mujer o No Aplica. Avisos Pendientes: use Alta, Baja o Modificacion de Salario."],
      ["10. Codigo Postal: debe tener 5 dígitos; se usa para resolver Estado/Municipio/Colonia automáticamente."],
      ["11. Lider Directo: escriba el Numero (mismo valor de la columna Numero) de otro empleado activo de la empresa."],
      ["12. Codigo: debe ser único dentro de la empresa; no puede repetirse ni coincidir con el de otro empleado."],
    ];
    const wsInstr = XLSX.utils.aoa_to_sheet(instrucciones);
    wsInstr["!cols"] = [{ wch: 95 }];
    if (wsInstr["A1"]) {
      wsInstr["A1"].s = { font: { bold: true, sz: 12, name: "Arial" } };
    }
    XLSX.utils.book_append_sheet(wb, wsInstr, "Instrucciones");

    XLSX.writeFile(wb, "edicion_masiva_empleados.xlsx");
  }

  onChangeArchivo(e) {
    this.setState({ loading: true });
    if (
      e.file.type ===
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    ) {
      this.setState({
        archivosExcel: [{ ...e.file, name: e.file.name, status: "uploading" }],
      });
      this.cargarArchivoExcel(e.file);
    } else {
      message.error("El archivo debe ser un archivo de Excel (.xlsx)");
      this.setState({ loading: false });
    }
  }

  cargarArchivoExcel(file) {
    const reader = new FileReader();
    reader.readAsArrayBuffer(file);
    reader.onloadend = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const primeraHoja = workbook.SheetNames[0];
      const filas = XLSX.utils.sheet_to_json(workbook.Sheets[primeraHoja], { defval: "" });
      this.procesarFilas(filas).then((result) => {
        this.setState({
          data: result.data,
          columnasPresentes: result.columnasPresentes,
          errores: result.errores,
          archivoError: result.errores > 0 ? result.archivoErrores : null,
          sinIdError: result.sinIdError,
          archivosExcel: [{ ...file, name: file.name, status: "success" }],
          loading: false,
          erroresBackend: [],
        });
        if (result.sinIdError) {
          message.error(
            "El archivo no contiene la columna ID. Descargue el Excel desde este módulo e intente nuevamente."
          );
        } else if (result.errores > 0) {
          message.warning(
            `Se detectaron ${result.errores} errores. Descargue el archivo de errores para revisarlos.`
          );
        } else {
          const conCambios = result.data.filter((d) => d._hayCambios).length;
          message.success(
            `Archivo leído: ${conCambios} empleado(s) con cambios de ${result.data.length} leídos.`
          );
        }
      });
    };
  }

  async procesarFilas(filas) {
    if (!filas.length)
      return {
        data: [],
        columnasPresentes: [],
        errores: 0,
        sinIdError: false,
        archivoErrores: null,
      };

    const { empleadosMap } = this.state;

    const primeraFila = filas[0];
    if (!("ID" in primeraFila)) {
      return {
        data: [],
        columnasPresentes: [],
        errores: 0,
        sinIdError: true,
        archivoErrores: null,
      };
    }

    const columnasPresentes = TODAS_EDITABLES.filter((col) => col in primeraFila);

    // Un índice por catálogo presente, en vez de un find() lineal por celda.
    const indicesCatalogo = {};
    for (const cfg of CATALOGOS_CONFIG) {
      if (columnasPresentes.includes(cfg.col)) {
        indicesCatalogo[cfg.col] = construirIndiceCatalogo(this.state[cfg.stateKey] || [], cfg);
      }
    }

    // Pre-cómputo para validar "Codigo" (duplicados en archivo y contra existentes)
    const codigosEnArchivo = new Map();
    if (columnasPresentes.includes("Codigo")) {
      for (const fila of filas) {
        const v = String(fila["Codigo"] ?? "").trim();
        if (v) codigosEnArchivo.set(v, (codigosEnArchivo.get(v) || 0) + 1);
      }
    }
    const codigoOwnerMap = new Map();
    for (const e of Object.values(empleadosMap)) {
      if (e.codigo != null) codigoOwnerMap.set(String(e.codigo), String(e._id));
    }

    const erroresRows = [];
    const data = [];
    let errores = 0;

    for (const fila of filas) {
      const encodedId = String(fila["ID"] || "").trim();
      const decodedId = encodedId ? decodeId(encodedId) : null;
      const emp = decodedId ? empleadosMap[decodedId] : null;
      if (!encodedId || !emp) {
        errores++;
        erroresRows.push({
          ...fila,
          Errores: !encodedId ? "ID vacío" : `ID no encontrado: ${encodedId}`,
        });
        continue;
      }

      const actual = emp;
      const empleadoIdStr = String(emp._id);
      const obj = {
        empleadoId: empleadoIdStr,
        numero: actual.serieNumero || "",
        nombreCompleto: nombreCompleto(actual),
      };
      const cambios = [];
      const filaErrores = [];

      for (const col of columnasPresentes) {
        const val = String(fila[col] ?? "").trim();

        if (COLUMNAS_TEXTO_REQUERIDAS.has(col) && !val) {
          filaErrores.push(`${col}: no puede estar vacío`);
          continue;
        }

        if (col === "Codigo Postal") {
          if (val === "") {
            obj.cp = "";
          } else if (!/^\d{5}$/.test(val)) {
            filaErrores.push(`Codigo Postal: "${val}" debe tener 5 dígitos`);
            continue;
          } else {
            obj.cp = val;
          }
          obj["_d_Codigo Postal"] = val;
          const cpActual = actual.coloniaId?.cp || "";
          if (cpActual !== val) cambios.push(col);
        } else if (col === "Codigo") {
          if (val === "") {
            obj.codigo = null;
            obj._d_Codigo = "";
            if (actual.codigo != null) cambios.push(col);
          } else if ((codigosEnArchivo.get(val) || 0) > 1) {
            filaErrores.push(`Codigo: "${val}" duplicado en el archivo`);
            continue;
          } else if (
            codigoOwnerMap.has(val) &&
            codigoOwnerMap.get(val) !== empleadoIdStr
          ) {
            filaErrores.push(`Codigo: "${val}" ya existe`);
            continue;
          } else {
            const num = Number(val);
            if (isNaN(num)) {
              filaErrores.push(`Codigo: "${val}" no es un número`);
              continue;
            }
            obj.codigo = num;
            obj._d_Codigo = val;
            if (String(actual.codigo ?? "") !== val) cambios.push(col);
          }
        } else if (col === "Lider Directo") {
          if (val === "") {
            obj.liderDirectoId = null;
            obj["_d_Lider Directo"] = "";
            if (actual.liderDirectoId) cambios.push(col);
          } else {
            const encontrado = Object.values(empleadosMap).find(
              (e) => (e.serieNumero || "").trim() === val
            );
            if (!encontrado) {
              filaErrores.push(`Lider Directo: "${val}" no encontrado`);
              continue;
            }
            if (String(encontrado._id) === empleadoIdStr) {
              filaErrores.push("Lider Directo: un empleado no puede ser su propio líder");
              continue;
            }
            obj.liderDirectoId = encontrado._id;
            obj["_d_Lider Directo"] = val;
            const idActual = String(actual.liderDirectoId?._id || actual.liderDirectoId || "");
            if (idActual !== String(encontrado._id)) cambios.push(col);
          }
        } else if (CAMPO_TEXTO[col] !== undefined) {
          const campo = CAMPO_TEXTO[col];
          obj[campo] = val;
          obj[`_d_${col}`] = val;
          if (String(actual[campo] || "") !== val) cambios.push(col);
        } else if (CAMPO_NUMERO[col] !== undefined) {
          const campo = CAMPO_NUMERO[col];
          if (val === "") {
            obj[campo] = null;
            obj[`_d_${col}`] = "";
            if (actual[campo] != null) cambios.push(col);
          } else {
            const num = Number(String(val).replace(/,/g, ""));
            if (isNaN(num)) {
              filaErrores.push(`${col}: "${val}" no es un número`);
              continue;
            }
            obj[campo] = num;
            obj[`_d_${col}`] = String(num);
            if (String(actual[campo] ?? "") !== String(num)) cambios.push(col);
          }
        } else if (CAMPO_FECHA[col] !== undefined) {
          const campo = CAMPO_FECHA[col];
          if (val === "") {
            obj[campo] = null;
            obj[`_d_${col}`] = "";
            if (actual[campo]) cambios.push(col);
          } else if (!/^\d{4}-\d{2}-\d{2}$/.test(val)) {
            filaErrores.push(`${col}: "${val}" debe tener formato AAAA-MM-DD`);
            continue;
          } else {
            obj[campo] = val;
            obj[`_d_${col}`] = val;
            const actualStr = fechaISO(actual[campo]);
            if (actualStr !== val) cambios.push(col);
          }
        } else if (CAMPO_BOOL[col] !== undefined) {
          const campo = CAMPO_BOOL[col];
          const bv = boolVal(val);
          if (bv === null) {
            filaErrores.push(`${col}: valor inválido "${val}" (use SI o NO)`);
            continue;
          }
          obj[campo] = bv;
          obj[`_d_${col}`] = bv ? "SI" : "NO";
          if (bv !== !!actual[campo]) cambios.push(col);
        } else if (CAMPO_ENUM_NUM[col] !== undefined) {
          const { campo, labels } = CAMPO_ENUM_NUM[col];
          if (val === "") {
            obj[campo] = null;
            obj[`_d_${col}`] = "";
            if (actual[campo] != null) cambios.push(col);
          } else {
            const entry = buscarEtiqueta(labels, val);
            if (!entry) {
              filaErrores.push(`${col}: "${val}" inválido (use ${Object.values(labels).join(", ")})`);
              continue;
            }
            const clave = Number(entry[0]);
            obj[campo] = clave;
            obj[`_d_${col}`] = labels[clave];
            if (actual[campo] !== clave) cambios.push(col);
          }
        } else if (CAMPO_ENUM_STR[col] !== undefined) {
          const cfg = CAMPO_ENUM_STR[col];
          if (val === "") {
            obj[cfg.campo] = null;
            obj[`_d_${col}`] = "";
            if (actual[cfg.campo]) cambios.push(col);
          } else if (cfg.labels) {
            const entry = buscarEtiqueta(cfg.labels, val);
            if (!entry) {
              filaErrores.push(`${col}: "${val}" inválido (use ${Object.values(cfg.labels).join(", ")})`);
              continue;
            }
            obj[cfg.campo] = entry[0];
            obj[`_d_${col}`] = cfg.labels[entry[0]];
            if (actual[cfg.campo] !== entry[0]) cambios.push(col);
          } else {
            const encontrado = cfg.valores.find((v) => v.toLowerCase() === val.toLowerCase());
            if (!encontrado) {
              filaErrores.push(`${col}: "${val}" inválido (use ${cfg.valores.join(", ")})`);
              continue;
            }
            obj[cfg.campo] = encontrado;
            obj[`_d_${col}`] = encontrado;
            if (actual[cfg.campo] !== encontrado) cambios.push(col);
          }
        } else {
          const cfg = CATALOGOS_CONFIG.find((c) => c.col === col);
          if (!cfg) continue;
          if (val === "") {
            obj[cfg.campo] = null;
            obj[`_d_${col}`] = "";
            if (actual[cfg.campo]) cambios.push(col);
            continue;
          }
          const encontrado = indicesCatalogo[col]?.get(normalizarCatalogo(val));
          if (!encontrado) {
            filaErrores.push(`${col}: "${val}" no encontrado`);
            continue;
          }
          obj[cfg.campo] = encontrado._id;
          obj[`_d_${col}`] = String(encontrado[cfg.matchField] ?? val);
          const idActual = String(actual[cfg.campo]?._id || actual[cfg.campo] || "");
          if (idActual !== String(encontrado._id)) cambios.push(col);
        }
      }

      if (filaErrores.length) {
        errores++;
        erroresRows.push({ ...fila, Errores: filaErrores.join("; ") });
      } else {
        obj._cambios = cambios;
        obj._hayCambios = cambios.length > 0;
        data.push(obj);
      }
    }

    const archivoEditar = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      archivoEditar,
      XLSX.utils.json_to_sheet(erroresRows),
      "Errores"
    );
    return {
      data,
      columnasPresentes,
      errores,
      sinIdError: false,
      archivoErrores: archivoEditar,
    };
  }

  descargarArchivoErrores() {
    if (!this.state.archivoError) return;
    const wbout = XLSX.write(this.state.archivoError, {
      bookType: "xlsx",
      type: "binary",
    });
    const s2ab = (s) => {
      const buf = new ArrayBuffer(s.length);
      const view = new Uint8Array(buf);
      for (let i = 0; i < s.length; i++) view[i] = s.charCodeAt(i) & 0xff;
      return buf;
    };
    const a = document.createElement("a");
    document.body.appendChild(a);
    a.style = "display: none";
    const blob = new Blob([s2ab(wbout)], { type: "application/octet-stream" });
    const url = window.URL.createObjectURL(blob);
    a.href = url;
    a.download = "errores_edicion_masiva_empleados.xlsx";
    a.click();
    window.URL.revokeObjectURL(url);
  }

  onFinish() {
    const dataConCambios = this.state.data.filter((d) => d._hayCambios);
    if (!dataConCambios.length) {
      message.warning("No hay cambios que guardar");
      return;
    }
    const dataParaEnviar = dataConCambios.map((row) => {
      const obj = {};
      for (const key in row) {
        if (
          !key.startsWith("_d_") &&
          key !== "_cambios" &&
          key !== "_hayCambios" &&
          key !== "numero" &&
          key !== "nombreCompleto"
        ) {
          obj[key] = row[key];
        }
      }
      return obj;
    });

    this.setState({ loading: true, erroresBackend: [] });
    axios
      .post(
        "empleados/editarEmpleadosMasivo",
        {
          empresaId: this.props.empresaId,
          data: dataParaEnviar,
          columnas: this.state.columnasPresentes,
        },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        this.setState({ loading: false });
        if (res.data.success === 2) {
          const erroresBackend = res.data.errores || [];
          if (erroresBackend.length) {
            message.warning(
              `Se actualizaron ${res.data.numero} empleados. Se omitieron ${erroresBackend.length} por errores.`
            );
            this.setState({ erroresBackend });
          } else {
            message.success(`Se actualizaron ${res.data.numero} empleados`);
            this.atras();
          }
        } else {
          message.error("Hubo un error al actualizar los empleados");
        }
      });
  }

  render() {
    const { data, columnasPresentes, errores, sinIdError, soloConCambios, erroresBackend } = this.state;

    const totalConCambios = data.filter((d) => d._hayCambios).length;
    const dataMostrar = soloConCambios ? data.filter((d) => d._hayCambios) : data;

    const columnasIdentificacion = [
      { title: "Numero", dataIndex: "numero", key: "numero", fixed: "left", width: 100 },
      { title: "Nombre Completo", dataIndex: "nombreCompleto", key: "nombreCompleto", fixed: "left", width: 220 },
    ];

    const renderCelda = (col) => (_, record) => {
      const val = record[`_d_${col}`] ?? String(record[col] ?? "");
      const changed = record._cambios && record._cambios.includes(col);
      return (
        <span
          style={{
            backgroundColor: changed ? "#fff3cd" : undefined,
            padding: changed ? "2px 6px" : undefined,
            borderRadius: changed ? 4 : undefined,
            display: "inline-block",
          }}
        >
          {val}
        </span>
      );
    };

    const columnasEditables = columnasPresentes.map((col) => ({
      title: col,
      key: col,
      width: 170,
      render: renderCelda(col),
    }));

    const columns = [...columnasIdentificacion, ...columnasEditables];

    return (
      <>
        <PageHeader
          onBack={this.atras.bind(this)}
          title="Edición Masiva de Empleados"
          style={{
            border: "1px solid rgb(235, 237, 240)",
            backgroundColor: "white",
            marginBottom: 10,
          }}
          extra={
            this.state.archivoError ? (
              <ButtonItem onClick={this.descargarArchivoErrores.bind(this)}>
                Descargar Errores
              </ButtonItem>
            ) : null
          }
        />
        <div style={{ backgroundColor: "white", padding: 20, paddingTop: 5 }}>
          <Spin spinning={this.state.loading} tip="Cargando, espere por favor...">
            <Alert
              style={{ marginBottom: 16, marginTop: 10 }}
              type="info"
              showIcon
              message="Instrucciones"
              description={
                <ol style={{ margin: 0, paddingLeft: 20 }}>
                  <li>Descargue el Excel con los datos actuales de todos los empleados.</li>
                  <li>
                    <b>Borre las columnas que NO desee modificar.</b> Solo se
                    actualizarán las columnas que queden en el archivo.
                  </li>
                  <li>
                    La columna <b>ID</b> es obligatoria y no debe eliminarse ni
                    modificarse.
                  </li>
                  <li>
                    Revise la hoja <b>Instrucciones</b> del Excel descargado para el
                    formato exacto de cada columna (fechas, SI/NO, catálogos, etc.).
                  </li>
                  <li>
                    Suba el archivo modificado y revise la vista previa antes de
                    guardar.
                  </li>
                </ol>
              }
            />
            <Row style={{ marginBottom: 16, alignItems: "center" }}>
              <Col span={12}>
                <ButtonItem
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={this.descargarExcel.bind(this)}
                >
                  Descargar Excel
                </ButtonItem>
              </Col>
              <Col span={12}>
                <Upload
                  showUploadList={{ showRemoveIcon: false, showPreviewIcon: false }}
                  fileList={this.state.archivosExcel}
                  customRequest={this.onChangeArchivo.bind(this)}
                  accept=".xlsx"
                >
                  <ButtonItem icon={<UploadOutlined />}>
                    Subir Excel modificado
                  </ButtonItem>
                </Upload>
              </Col>
            </Row>

            {sinIdError && (
              <Alert
                type="error"
                showIcon
                message="El archivo no contiene la columna ID. Descargue el Excel desde este módulo y vuelva a intentarlo."
                style={{ marginBottom: 16 }}
              />
            )}

            {data.length > 0 && !sinIdError && (
              <>
                <Alert
                  type={errores > 0 ? "warning" : "success"}
                  showIcon
                  style={{ marginBottom: 12 }}
                  message={
                    <>
                      Se detectaron <b>{totalConCambios}</b> empleado(s) con
                      cambios en las columnas: <b>{columnasPresentes.join(", ")}</b>.
                      {errores > 0 && (
                        <>
                          {" "}Se omitieron <b>{errores}</b> fila(s) con errores.
                        </>
                      )}
                    </>
                  }
                />
                {erroresBackend && erroresBackend.length > 0 && (
                  <Alert
                    type="error"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message={`El backend rechazó ${erroresBackend.length} empleado(s):`}
                    description={
                      <ul style={{ margin: 0, paddingLeft: 20 }}>
                        {erroresBackend.map((e, idx) => {
                          const emp = this.state.empleadosMap[String(e.empleadoId)];
                          const nombre = emp ? nombreCompleto(emp) : e.empleadoId;
                          return (
                            <li key={idx}>
                              <b>{nombre}</b>: {e.motivo}
                            </li>
                          );
                        })}
                      </ul>
                    }
                  />
                )}
                <div
                  style={{
                    marginBottom: 8,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span>
                    <Switch
                      checked={soloConCambios}
                      onChange={(v) => this.setState({ soloConCambios: v })}
                      size="small"
                    />{" "}
                    Mostrar solo con cambios ({totalConCambios} de {data.length})
                  </span>
                  {totalConCambios > 0 && (
                    <ButtonItem type="primary" onClick={this.onFinish.bind(this)}>
                      Guardar cambios en {totalConCambios} empleado
                      {totalConCambios !== 1 ? "s" : ""}
                    </ButtonItem>
                  )}
                </div>
                <TableItem
                  size="small"
                  dataSource={dataMostrar}
                  columns={columns}
                  scroll={{ x: "max-content" }}
                  rowKey="empleadoId"
                />
              </>
            )}
          </Spin>
        </div>
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
});

export default connect(mapStateToProps)(EdicionMasivaEmpleados);
